import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Btn } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GitMerge, Users } from "lucide-react";

const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");

// Groups client login accounts by normalized name; lets admin merge duplicates into one.
export function MergeClientsDialog({ open, onClose, onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [primary, setPrimary] = useState({}); // groupKey -> chosen primary id
  const [busy, setBusy] = useState("");

  const load = useCallback(() => { api.get("/clients").then((r) => setAccounts(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const groups = Object.values(
    accounts.reduce((acc, a) => {
      const k = norm(a.name) || a.id;
      (acc[k] = acc[k] || { key: k, name: a.name, items: [] }).items.push(a);
      return acc;
    }, {})
  ).filter((g) => g.items.length > 1);

  const merge = async (g) => {
    const primaryId = primary[g.key] || g.items[0].id;
    const dups = g.items.map((x) => x.id).filter((id) => id !== primaryId);
    if (!dups.length) return;
    setBusy(g.key);
    try {
      const { data } = await api.post("/admin/clients/merge", { primary_id: primaryId, duplicate_ids: dups });
      toast.success(`Merged ${data.merged} duplicate account(s)`);
      await load();
      onDone && onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="merge-clients-dialog">
        <DialogHeader><DialogTitle className="font-head uppercase flex items-center gap-2"><GitMerge size={18} /> Merge Duplicate Client Logins</DialogTitle></DialogHeader>
        <p className="font-mono text-[11px] text-zinc-500">Accidental duplicate client accounts (from contractors signing up as clients). Pick the one to keep — jobs and directory links move to it, the rest are deleted.</p>
        <div className="mt-3 space-y-3 max-h-[55vh] overflow-y-auto">
          {groups.length === 0 && <div className="font-mono text-xs text-zinc-600 py-6 text-center"><Users className="inline mr-2" size={14} />No duplicate client logins found.</div>}
          {groups.map((g) => (
            <div key={g.key} data-testid={`merge-group-${g.key}`} className="border-2 border-zinc-800 p-3">
              <div className="font-head font-bold text-white mb-2">{g.name} <span className="font-mono text-[10px] text-[#FF5722]">×{g.items.length}</span></div>
              <div className="space-y-1.5">
                {g.items.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                    <input type="radio" name={`p-${g.key}`} checked={(primary[g.key] || g.items[0].id) === a.id}
                      onChange={() => setPrimary((p) => ({ ...p, [g.key]: a.id }))} />
                    <span className="truncate">{a.email || "(no email)"}</span>
                  </label>
                ))}
              </div>
              <Btn data-testid={`merge-do-${g.key}`} disabled={busy === g.key} onClick={() => merge(g)} className="mt-3 w-full">
                {busy === g.key ? "Merging…" : "Keep selected · merge the rest"}
              </Btn>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
